import { Check, X, Loader2, Copy } from 'lucide-react'
import { Button } from '../ui/button'
import { type TransactionHeaderProps } from '../../types/transactions'
import { canApproveTransaction } from '../../lib/transaction-utils'
import { SubNavBar, SubNavBarLeft, SubNavBarRight } from '../nav/SubNavBar'
import { CompanyRoleBadge, ExternalApiBadge, FlaggedBadge, StatusBadge } from '../badges/status'
import { toast } from 'sonner'
import { useState } from 'react'

export function TransactionHeader({
  transactionId,
  company,
  companyImageUrl,
  companyTradeType,
  status,
  transactionType,
  showActions = false,
  onApprove,
  onReject,
  isApproving = false,
  isRejecting = false,
  isFlagged = false,
  isComplianceUser = false,
  isExternalApi = false,
}: TransactionHeaderProps) {
  const isSwap = transactionType === 'TRADE'
  const canShowActions =
    showActions && canApproveTransaction(status, isComplianceUser, transactionType, isFlagged)
  const [isCopySuccess, setIsCopySuccess] = useState(false)

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address)
    toast.success('Address copied to clipboard!')
    setIsCopySuccess(true)
    setTimeout(() => {
      setIsCopySuccess(false)
    }, 2000)
  }

  return (
    <>
      <SubNavBar
        className={`hidden md:block py-2 px-6 rounded-full md:rounded-3xl border border-card-border bg-transparent md:bg-white ${isFlagged ? 'border border-destructive bg-destructive/10  md:border-none md:bg-white' : ''}`}
      >
        <SubNavBarLeft allowWrap={true} className="gap-2 flex-wrap">
          {companyImageUrl && (
            <img
              src={companyImageUrl}
              alt={`${company} logo`}
              className="w-[28px] h-[28px] rounded object-cover border border-card-border"
            />
          )}
          {company && (
            <>
              <div className="flex items-center gap-2">
                <h1 className="text-lg md:text-2xl font-bold">{company}</h1>
                <CompanyRoleBadge tradeType={companyTradeType} />
              </div>
              <h1 className="text-lg md:text-2xl font-bold">{'>'}</h1>
            </>
          )}
          <h1 className="text-lg md:text-2xl font-bold">{transactionId}</h1>
          <div className="flex items-center gap-2">
            <StatusBadge status={status} type={isSwap ? 'TRADE' : 'DEPOSIT'} />
            <FlaggedBadge flagged={isFlagged} />
            <ExternalApiBadge isExternalApi={isExternalApi} />
          </div>
        </SubNavBarLeft>
        {canShowActions ? (
          <SubNavBarRight className="hidden md:flex">
            <>
              <Button
                onClick={onApprove}
                variant="main_action"
                className="bg-main/90 hover:bg-main h-9 rounded-sm"
                disabled={isApproving}
              >
                {isApproving ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-1" />
                )}
                {isApproving ? 'Approving...' : 'Approve'}
              </Button>
              <Button variant="outline" onClick={onReject} className="h-9" disabled={isRejecting}>
                {isRejecting ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <X className="w-4 h-4 mr-1" />
                )}
                {isRejecting ? 'Rejecting...' : 'Reject'}
              </Button>
            </>
          </SubNavBarRight>
        ) : null}
      </SubNavBar>

      <div
        className={`block md:hidden py-2 px-6 rounded-full border border-card-border bg-transparent ${isFlagged ? 'border border-destructive bg-destructive/10' : ''}`}
      >
        <SubNavBarLeft
          allowWrap={false}
          hideBack={true}
          className={`gap-2 overflow-hidden min-w-0`}
        >
          <StatusBadge status={status} type={isSwap ? 'TRADE' : 'DEPOSIT'} />
          <ExternalApiBadge isExternalApi={isExternalApi} />
          <h1 className="block text-lg md:text-2xl font-normal flex-1 w-auto min-w-0 truncate whitespace-nowrap">
            {transactionId}
          </h1>
          <Button
            onClick={() => handleCopyAddress(transactionId)}
            variant="main_outline"
            size="sm"
            className="border-transparent px-0 shadow-none hover:shadow-none  has-[>svg]:px-0 flex-0"
          >
            {isCopySuccess ? <Check className="size-5" /> : <Copy className="size-5" />}
          </Button>
        </SubNavBarLeft>
      </div>
    </>
  )
}
